import React, { useEffect, useState } from 'react';
import { Star, Loader2, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { clientPost, errorMessage, isUnauthorized, fmtDate } from '../../lib/clientApi';

const LABELS = { 1: 'Très insatisfait(e)', 2: 'Insatisfait(e)', 3: 'Correct', 4: 'Satisfait(e)', 5: 'Très satisfait(e)' };

export function RatingDialog({ event, onClose, onDone, onUnauthorized }) {
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (event) { setStars(0); setHover(0); setComment(''); } }, [event]);

  const submit = async () => {
    if (!stars) return toast.error('Choisissez une note de 1 à 5 étoiles');
    setLoading(true);
    try {
      await clientPost(`/api/client/interventions/${event.id}/rating`, { stars, comment: comment.trim() });
      onDone();
    } catch (err) {
      if (isUnauthorized(err)) return onUnauthorized();
      toast.error(errorMessage(err, "Impossible d'enregistrer votre avis"));
    } finally { setLoading(false); }
  };

  const shown = hover || stars;
  return (
    <Dialog open={!!event} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="client-rating-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Space_Grotesk'] text-xl">Votre avis</DialogTitle>
          <DialogDescription className="text-base">
            {event?.label}{event?.start && <> · <span className="capitalize">{fmtDate(event.start, { day: 'numeric', month: 'long' })}</span></>}{event?.easer_name && <> · avec <span className="notranslate">{event.easer_name}</span></>}.<br />Votre retour aide votre Easer et améliore notre qualité de service.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div>
            <div className="flex items-center justify-center gap-2" role="radiogroup" aria-label="Note sur 5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" role="radio" aria-checked={stars === n} aria-label={`Donner ${n} sur 5`} onClick={() => setStars(n)} onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)}
                  className="h-12 w-12 rounded-md border border-border bg-background hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))] flex items-center justify-center transition-colors" data-testid={`client-rating-star-button-${n}`}>
                  <Star className={`h-7 w-7 transition-colors ${n <= shown ? 'fill-[hsl(var(--brand-apricot))] text-[hsl(var(--brand-apricot))]' : 'text-muted-foreground'}`} />
                </button>
              ))}
            </div>
            <p className="text-center text-base text-muted-foreground mt-2 min-h-[24px]" data-testid="client-rating-label">{shown ? LABELS[shown] : 'Touchez une étoile'}</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="rating-comment" className="text-base">Commentaire (optionnel)</Label>
            <Textarea id="rating-comment" rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Ex. Ponctuel(le), travail soigné, très agréable." className="text-base" data-testid="client-rating-comment-textarea" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" className="h-11" onClick={onClose} data-testid="client-rating-cancel-button">Annuler</Button>
          <Button className="h-11" onClick={submit} disabled={loading || !stars} data-testid="client-rating-submit-button">
            {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />} Envoyer mon avis
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
